// Motor quote fields on the generic offer form.
//
// ─── Why these live here and not in a fourth product form ─────────────
//
// PgmaOfferForm / RcProOfferForm / PgmOfferForm are RATING forms: each one
// owns a tariff, calls its own pricing endpoint and renders a computed
// premium the underwriter cannot overrule. Motor has no approved tariff on
// this platform, so it is not one of those. It is the generic form plus the
// fields a motor proposal has to state — vehicle, cover basis, driver — with
// the premium keyed by hand.
//
// Keeping it inside the generic drawer is what makes "other products keep
// the current minimal form" true by construction: this block renders only
// when MOTOR is the selected line of business, and nothing else on the
// drawer changes shape.
//
// Defined at module scope, like FormField in NewOfferPanel, because a
// component declared inside a render function is a new type on every
// keystroke — React unmounts and remounts it, and the input loses focus
// after each character.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { S } from '../regureStyles';
import { MOTOR_COVERAGE_TYPES, MOTOR_NCD_OPTIONS } from './useNewOfferFunctions';
import { annualPeriodEnd } from './offerDefaults';

const sectionStyle = {
  fontSize: 11,
  fontWeight: 700,
  color: S.textMuted,
  textTransform: 'uppercase',
  letterSpacing: 0.7,
  margin: '22px 0 12px',
  paddingBottom: 6,
  borderBottom: `1px solid ${S.borderLight}`,
};

const Field = ({ label, labelStyle, children }) => (
  <div style={{ marginBottom: 14 }}>
    <label style={labelStyle}>{label}</label>
    {children}
  </div>
);

// Two fields per row wherever both are short. A motor drawer is a long form
// and a single column turns it into a scroll marathon.
const Pair = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>
);

const MotorOfferFields = ({ motor, setMotorField, currency, focusedRef, inputStyle, labelStyle }) => {
  const { t } = useTranslation();
  const onFocus = e => { if (focusedRef) focusedRef.current = e.target; };
  const text = (key, extra = {}) => (
    <input
      value={motor[key]}
      onChange={e => setMotorField(key, e.target.value)}
      onFocus={onFocus}
      style={inputStyle}
      {...extra}
    />
  );
  const selectStyle = { ...inputStyle, cursor: 'pointer' };
  // Name the currency or name none — never "(BHD)" on a tenant configured
  // in something else, and never an empty "()" before the config lands.
  const valueLabel = currency
    ? t('offers.newOffer.motor.vehicleValue', { currency })
    : t('offers.newOffer.motor.vehicleValuePlain');
  // Shown, not stored twice: the end date is derived from the inception so
  // the underwriter can see the period they are quoting before they save it.
  const periodEnd = annualPeriodEnd(motor.effectiveDate);

  return (
    <>
      <div style={sectionStyle}>{t('offers.newOffer.motor.vehicleSection')}</div>
      <Pair>
        <Field labelStyle={labelStyle} label={t('offers.newOffer.motor.make')}>
          {text('vehicleMake', { placeholder: t('offers.newOffer.motor.makePh') })}
        </Field>
        <Field labelStyle={labelStyle} label={t('offers.newOffer.motor.model')}>
          {text('vehicleModel', { placeholder: t('offers.newOffer.motor.modelPh') })}
        </Field>
      </Pair>
      <Pair>
        <Field labelStyle={labelStyle} label={t('offers.newOffer.motor.year')}>
          {text('yearOfManufacture', { type: 'number', min: 1900, max: 2100, placeholder: '2024' })}
        </Field>
        <Field labelStyle={labelStyle} label={t('offers.newOffer.motor.plate')}>
          {text('plateNumber', { placeholder: '12345' })}
        </Field>
      </Pair>
      <Field labelStyle={labelStyle} label={t('offers.newOffer.motor.chassis')}>
        {text('chassisNumber', { placeholder: t('offers.newOffer.motor.chassisPh') })}
      </Field>
      <Field labelStyle={labelStyle} label={valueLabel}>
        {text('vehicleValue', { type: 'number', min: 0, step: '0.001' })}
      </Field>

      <div style={sectionStyle}>{t('offers.newOffer.motor.coverSection')}</div>
      <Field labelStyle={labelStyle} label={t('offers.newOffer.motor.coverageType')}>
        <select
          value={motor.coverageType}
          onChange={e => setMotorField('coverageType', e.target.value)}
          onFocus={onFocus}
          style={selectStyle}
        >
          {MOTOR_COVERAGE_TYPES.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </Field>
      <Field labelStyle={labelStyle} label={t('offers.newOffer.motor.effectiveDate')}>
        <input
          type='date'
          value={motor.effectiveDate}
          onChange={e => setMotorField('effectiveDate', e.target.value)}
          onFocus={onFocus}
          style={inputStyle}
        />
        {periodEnd && (
          <div style={{ fontSize: 11, color: S.textMuted, marginTop: 5 }}>
            {t('offers.newOffer.motor.periodTo', { date: periodEnd })}
          </div>
        )}
      </Field>

      <div style={sectionStyle}>{t('offers.newOffer.motor.driverSection')}</div>
      <Field labelStyle={labelStyle} label={t('offers.newOffer.motor.driverName')}>
        {text('driverName')}
      </Field>
      <Pair>
        <Field labelStyle={labelStyle} label={t('offers.newOffer.motor.dateOfBirth')}>
          <input
            type='date'
            value={motor.driverDateOfBirth}
            onChange={e => setMotorField('driverDateOfBirth', e.target.value)}
            onFocus={onFocus}
            style={inputStyle}
          />
        </Field>
        <Field labelStyle={labelStyle} label={t('offers.newOffer.motor.licenseNumber')}>
          {text('licenseNumber')}
        </Field>
      </Pair>
      <Field labelStyle={labelStyle} label={t('offers.newOffer.motor.ncd')}>
        <select
          value={motor.noClaimsDiscount}
          onChange={e => setMotorField('noClaimsDiscount', e.target.value)}
          onFocus={onFocus}
          style={selectStyle}
        >
          {MOTOR_NCD_OPTIONS.map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </Field>
    </>
  );
};

export default MotorOfferFields;
